'use client';

import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { RequestedDataRenderer } from '@components';
import { CONSTANT } from '@const';
import { AccountContext } from '@context';
import { TermsAndConditionsService } from '@data';
import { msalService } from '@services';
import { safeId, useLogout } from '@utils';

import { Button, Checkbox, LoadingSpinner } from '@/componentsQGDS';
import { Modal } from '@/componentsQGDS/ui/Modal';

export const TermsModal = () => {
	// Context
	const { getSelectedAccount } = useContext(AccountContext);
	// State
	const { inProgress } = useMsal();
	const contentRef = useRef<HTMLDivElement>(null);
	const [openTC, setOpenTC] = useState<boolean>(false);
	const [isRead, setIsRead] = useState(false);
	const [isScrollable, setIsScrollable] = useState(false);
	const [isAgreed, setIsAgreed] = useState<boolean>(false);
	const [declineMode, setDeclineMode] = useState(false);
	// Others
	const logout = useLogout();

	// Data
	const getTermsConditions = useQuery({
		queryKey: [CONSTANT.TERMS_AND_CONDITIONS_API_URL],
		queryFn: () => TermsAndConditionsService.getTermsAndConds(),
		enabled:
			msalService.isValidAccount() &&
			!getSelectedAccount?.isLoading &&
			(!getSelectedAccount?.data || !getSelectedAccount?.data.isCurrentTandC),
	});
	const acceptTermsAndConds = useMutation({
		mutationFn: (termsAndConditionsId: string) =>
			TermsAndConditionsService.acceptTermsAndConds(termsAndConditionsId),
	});

	// Life cycle
	useEffect(() => {
		if (inProgress === InteractionStatus.None) {
			const isDataLoadOnError =
				getTermsConditions.data && !getSelectedAccount?.isLoading && !getSelectedAccount?.data;
			const isDataLoad = getTermsConditions.data && getSelectedAccount?.data;
			const hideOnInitialization = !getTermsConditions.isError && !isDataLoad;
			if (isDataLoadOnError) {
				setOpenTC(true);
			} else if (hideOnInitialization) {
				setOpenTC(false);
			} else {
				setOpenTC(!getSelectedAccount?.data?.isCurrentTandC);
			}
		}
	}, [
		inProgress,
		getTermsConditions.data,
		getSelectedAccount?.data,
		getTermsConditions.isError,
		getSelectedAccount?.isLoading,
	]);

	/**
	 * Checks if the content within the referenced element is scrollable.
	 * If the content is not scrollable, it sets the `isRead` state to true.
	 * The check is performed after a short delay to ensure the content is fully rendered.
	 * Additionally, the scrollable check is re-evaluated on window resize events.
	 */
	useEffect(() => {
		const checkScrollable = () => {
			if (contentRef.current) {
				const isContentScrollable =
					contentRef.current.scrollHeight > contentRef.current.clientHeight;
				setIsScrollable(isContentScrollable);
				setIsRead(!isContentScrollable);
			}
		};

		// Check after a short delay to ensure content is rendered
		const timeoutId = setTimeout(checkScrollable, 100);
		// Also check on window resize
		window.addEventListener('resize', checkScrollable);

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', checkScrollable);
		};
	}, [getTermsConditions.data?.content, openTC]);

	// Helper
	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
		const isBottom = scrollHeight - scrollTop <= clientHeight + 80;
		if (!isRead) {
			setIsRead(isBottom);
		}
	};

	const readAgain = () => {
		setDeclineMode(false);
		setIsRead(isScrollable ? false : true);
		setIsAgreed(false);
		if (!getTermsConditions.data) {
			getTermsConditions.refetch();
		}
	};

	const retry = () => {
		if (getTermsConditions.isError) {
			getTermsConditions.refetch();
		} else if (acceptTermsAndConds.isError) {
			accept();
		}
	};

	const accept = async () => {
		await acceptTermsAndConds.mutateAsync(safeId(getTermsConditions.data?.id)).then(() => {
			setOpenTC(false);
		});
	};

	const TermsAndCondsContent = getTermsConditions.data ? (
		<div className="flex flex-1 flex-col gap-3 overflow-hidden">
			<div
				ref={contentRef}
				className="border-light-border-alt flex-1 overflow-y-scroll rounded-xs border-2 p-6"
				onScroll={handleScroll}
				dangerouslySetInnerHTML={{
					__html: getTermsConditions.data.content,
				}}
			/>
			{acceptTermsAndConds.isError && (
				<p className="text-status-error text-xs">
					The server encountered an error while accepting the Terms and Conditions. Please try again
					later.
				</p>
			)}
		</div>
	) : (
		<></>
	);

	return (
		<>
			<Modal isOpen={openTC} onOpenChange={setOpenTC}>
				<Modal.Content size={declineMode ? 'xl' : '4xl'}>
					<Modal.Body className="pt-6">
						{declineMode ? (
							<p>To access the WaterIQ website, please read and accept the terms and conditions.</p>
						) : (
							<div className="flex h-[75vh] max-h-[75vh] flex-col gap-6 overflow-y-auto p-2 md:max-h-150">
								<RequestedDataRenderer
									fulfilledComponent={TermsAndCondsContent}
									emptyComponent={<></>}
									loaderComponent={
										<div className="border-light-border-alt flex h-full w-full items-center justify-center rounded-xs border-2 p-6">
											<LoadingSpinner label="Loading..." />
										</div>
									}
									errorComponent={
										<div className="border-light-border-alt flex h-full w-full flex-col items-center justify-center rounded-xs border-2 p-6">
											<p>
												The server encountered an error while retrieving the Terms and Conditions.
											</p>
											<br />
											<p>Please try again later.</p>
										</div>
									}
									queryStatus={getTermsConditions.status}
									data={getTermsConditions.data}
									error={getTermsConditions.error}
								/>
								<Checkbox
									isDisabled={!isRead}
									isSelected={isAgreed}
									onChange={isSelected => setIsAgreed(isSelected)}
									className={getTermsConditions.isSuccess ? 'visible' : 'hidden'}
								>
									<span>
										I agree with the&nbsp;<b>Terms and Conditions</b>
									</span>
								</Checkbox>
							</div>
						)}
					</Modal.Body>
					<Modal.Footer>
						{declineMode ? (
							<>
								<Button
									onClick={logout}
									aria-label="Logout"
									variant="secondary"
									className="w-full md:max-w-38"
								>
									Log out
								</Button>
								<Button onClick={readAgain} variant="primary" className="w-full md:max-w-38">
									Read again
								</Button>
							</>
						) : (
							<>
								{getTermsConditions.isError || acceptTermsAndConds.isError ? (
									<>
										<Button onClick={logout} variant="secondary" className="w-full md:max-w-38">
											Cancel
										</Button>
										<Button onClick={retry} variant="primary" className="w-full md:max-w-38">
											Retry
										</Button>
									</>
								) : (
									<>
										<Button
											onClick={() => setDeclineMode(true)}
											variant="secondary"
											className="w-full md:max-w-38"
										>
											{getTermsConditions.isLoading ? 'Cancel' : 'Decline'}
										</Button>
										<Button
											onClick={accept}
											isDisabled={!isAgreed}
											variant="primary"
											isLoading={acceptTermsAndConds.isPending}
											className="w-full md:max-w-38"
										>
											Accept
										</Button>
									</>
								)}
							</>
						)}
					</Modal.Footer>
				</Modal.Content>
			</Modal>
		</>
	);
};
